import { useMemo, useState } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import { isStreamConfigForbidden, type StreamConfig } from '@bloo-team/api-client'
import { Button, Card } from '@bloo-team/ui'
import { isDemoSession, useStreams, type Stream } from '@bloo-team/core-shell'
import {
  RETENTION_ROLE_NOTE, SCOPE_FORBIDDEN_NOTE, applyEffectiveRetention, canEditClusterRetention, effectiveRetention,
  retentionGateReason, retentionSummary, rowDrifts, useSetClusterRetention, useSetScopeRetention, useStreamConfig,
  type RetentionLevel,
} from '../lib/retention'

/**
 * Storage · Retention — the cluster default, the scope override, and what
 * every stream reports (docs/storage-requirements.md §Retention tab, revised
 * by the R1GET / R1PUT / R2 captures in docs/inventory/storage-capture.json).
 *
 * Retention is ONE object per level, not per stream: `stream-config` at the
 * cluster answers `{ retention: 90, replication: 1 }` and every
 * stream-management row echoes it. So the tab is two editor cards over a
 * read-only table — a row that differs from the effective value carries a
 * `drift` chip (none today; the day the backend diverges, it shows).
 *
 * Gates, in order:
 *   - not Root / TenantAdmin  → inputs read-only, Apply disabled, the role
 *                               note (a Security Engineer was refused: R1PUT)
 *   - live, pre-capture       → inputs editable, Apply DISABLED with the
 *                               reason as its wrapper title and a note
 *   - demo                    → the write lands in the overlay's level slot;
 *                               the card and the table re-render
 *   - the scope read refused  → the scope card has no inputs, only the
 *                               "needs a higher role" note (R2)
 */

export interface RetentionTabProps {
  clusterId: string
  scopeId: string
}

type Draft = { retention: string; replication: string }

const asInt = (v: string): number | null => {
  if (v.trim() === '') return null
  const n = Number(v)
  return Number.isInteger(n) && n >= 0 ? n : null
}

const str = (v: number | null | undefined): string => (v === null || v === undefined ? '' : String(v))

const draftOf = (c: StreamConfig | null): Draft => ({ retention: str(c?.retention), replication: str(c?.replication) })

const days = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v}d`)

interface LevelCardProps {
  level: RetentionLevel
  title: string
  eyebrow: string
  /** Current value at this level; null = nothing set here. */
  values: StreamConfig | null
  /** Shown in empty inputs — the value that applies when this level is unset. */
  fallback: StreamConfig | null
  /** The read was refused (scope only): no inputs. */
  forbidden?: boolean
  emptyNote?: string
  canEdit: boolean
  gated: boolean
  mutation: UseMutationResult<void, Error, StreamConfig>
}

function LevelCard({ level, title, eyebrow, values, fallback, forbidden, emptyNote, canEdit, gated, mutation }: LevelCardProps) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const base = draftOf(values)
  const d = draft ?? base
  const dirty = draft !== null && (asInt(d.retention) !== (values?.retention ?? null) || asInt(d.replication) !== (values?.replication ?? null))
  const reason = gated ? retentionGateReason(level) : !canEdit ? RETENTION_ROLE_NOTE : undefined
  const name = level === 'cluster' ? 'Cluster' : 'Scope'

  const apply = async () => {
    await mutation.mutateAsync({ retention: asInt(d.retention), replication: asInt(d.replication) })
    setDraft(null)
  }

  return (
    <div className="hud-span-6" role="group" aria-label={title}>
      <Card eyebrow={eyebrow} title={title} titleAs="h3">
        <div className="hud-stack">
          {forbidden ? (
            <p className="hud-unit st-gate" role="note">{SCOPE_FORBIDDEN_NOTE}</p>
          ) : (
            <>
              {values === null && emptyNote ? <p className="hud-unit" role="status">{emptyNote}</p> : null}
              <div className="hud-row st-bulk">
                <label className="hud-row st-field">
                  <span className="hud-unit">retention (days)</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="bui-input st-num"
                    aria-label={`${name} retention days`}
                    value={d.retention}
                    placeholder={str(fallback?.retention)}
                    readOnly={!canEdit}
                    onChange={(e) => setDraft({ ...d, retention: e.target.value })}
                  />
                </label>
                <label className="hud-row st-field">
                  <span className="hud-unit">replication</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="bui-input st-num"
                    aria-label={`${name} replication`}
                    value={d.replication}
                    placeholder={str(fallback?.replication)}
                    readOnly={!canEdit}
                    onChange={(e) => setDraft({ ...d, replication: e.target.value })}
                  />
                </label>
                {/* react-aria strips `title`; the wrapper carries the gate reason for hover. */}
                <span className="st-gated" title={reason}>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={level === 'cluster' ? 'Apply cluster default' : 'Apply scope override'}
                    isDisabled={gated || !canEdit || !dirty || mutation.isPending}
                    onPress={() => void apply()}
                  >
                    {mutation.isPending ? 'Applying…' : 'Apply'}
                  </Button>
                </span>
              </div>
              {mutation.isError ? (
                <div className="bui-alert bui-alert-error" role="alert">
                  <span className="bui-alert-mark">✕</span>
                  <span>
                    <span className="bui-alert-title">
                      {isStreamConfigForbidden(mutation.error) ? 'Needs a higher role' : 'Could not save'}
                    </span>
                    {isStreamConfigForbidden(mutation.error)
                      ? `The server refused this write for your role — ${RETENTION_ROLE_NOTE}.`
                      : mutation.error.message}
                  </span>
                </div>
              ) : null}
              {reason ? <p className="hud-unit st-gate" role="note">{reason}</p> : null}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

export function RetentionTab({ clusterId, scopeId }: RetentionTabProps) {
  const demo = isDemoSession()
  const scope = { clusterId, scopeId }
  const canEdit = canEditClusterRetention(clusterId, scopeId)
  const gated = !demo

  const cfg = useStreamConfig(scope)
  const streamsQ = useStreams(scope)
  const setCluster = useSetClusterRetention(scope)
  const setScope = useSetScopeRetention(scope)

  const effective = useMemo(() => effectiveRetention(cfg.cluster, cfg.scope), [cfg.cluster, cfg.scope])
  const rows = useMemo<Stream[]>(() => {
    const base = streamsQ.data?.streams ?? []
    // Demo: the fixture backend cannot change, so the rows adopt the effective value — the
    // twin of stream-management echoing the config. Live: the rows are what the backend said.
    const seen = demo ? applyEffectiveRetention(base, effective) : base
    return [...seen].sort((a, b) => a.stream.localeCompare(b.stream))
  }, [streamsQ.data, demo, effective])
  const summary = useMemo(() => retentionSummary(rows, cfg), [rows, cfg])
  const scopeForbidden = cfg.scope === 'forbidden'
  const scopeValues: StreamConfig | null = cfg.scope === 'forbidden' ? null : cfg.scope

  return (
    <div className="hud-stack" role="tabpanel" aria-label="Retention">
      <div className="hud-row" role="status" aria-label="Effective retention">
        <span className="hud-unit">effective</span>
        <span className="hud-value" style={{ fontSize: '0.9rem' }}>{days(effective?.retention)}</span>
        <span className="hud-unit">· replication {effective?.replication ?? '—'}</span>
        <span className="hud-unit">
          · {summary.streamsDiffering} of {rows.length} streams differ
        </span>
        {cfg.isFetching || streamsQ.isFetching ? <span className="hud-unit">· refreshing…</span> : null}
      </div>

      {cfg.isError ? (
        <div className="bui-alert bui-alert-error" role="alert">
          <span className="bui-alert-mark">✕</span>
          <span>
            <span className="bui-alert-title">Could not read the cluster stream config</span>
            {cfg.error?.message ?? 'Unknown error.'}
          </span>
        </div>
      ) : null}

      <div className="hud-grid">
        <LevelCard
          level="cluster"
          eyebrow="GET mgmt/core/stream-config · one object, cluster-wide"
          title="Cluster default"
          values={cfg.cluster}
          fallback={null}
          canEdit={canEdit}
          gated={gated}
          mutation={setCluster}
        />
        <LevelCard
          level="scope"
          eyebrow="mgmt/scope/{scopeId}/stream-config · precedence CB-33"
          title={`Scope override for ${scopeId}`}
          values={scopeValues}
          fallback={cfg.cluster}
          forbidden={scopeForbidden}
          emptyNote="no override, cluster default applies"
          canEdit={canEdit}
          gated={gated}
          mutation={setScope}
        />
      </div>

      {streamsQ.isPending ? <p className="hud-unit" role="status">Loading streams…</p> : null}

      {rows.length > 0 ? (
        <div className="dense-table-wrap">
          <table className="dense-table st-table">
            <thead>
              <tr>
                <th>Stream</th>
                <th className="num">Retention (days)</th>
                <th className="num">Replication</th>
                <th>Compression</th>
                <th aria-label="Drift" />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const drift = rowDrifts(s, effective)
                return (
                  <tr key={s.stream} data-drift={drift ? 'true' : undefined} data-severity={drift ? 'low' : 'info'}>
                    <td><span className="cell-name">{s.stream}</span></td>
                    <td className="num mono">{s.retention ?? '—'}</td>
                    <td className="num mono">{s.replication ?? '—'}</td>
                    <td className="mono muted">{s.compression ?? '—'}</td>
                    <td>
                      {drift ? (
                        <span
                          className="st-state"
                          data-state="drift"
                          title={`reports ${days(s.retention)} · replication ${s.replication ?? '—'}; effective ${days(effective?.retention)} · ${effective?.replication ?? '—'}`}
                        >
                          drift
                        </span>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <p className="hud-unit st-gate">
        Rows are what stream-management reports per stream; the effective value is the scope override, else the cluster default.
      </p>
    </div>
  )
}
